import { useContext, useEffect, useMemo, useRef } from 'react';

import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';
import { InsightsView } from 'contextProviders/InsightsContentProvider/viewTypes';

// Publishes the LIVE view of the content the user is looking at — effective date
// range, applied filters, gadget dimensions — so the AI Insights Chat can ground
// answers in what's on screen. The sibling of useRegisterInsightsContent, which
// publishes the entity itself.
//
// The two are separate because they must be called at different depths: the
// entity registers above the metric/vis gates so it survives loading, while the
// view has to sit inside FilterPlatesProvider to see live filters at all.
//
// Pass null to register nothing (popups, wallboards, headless renders).
const useRegisterInsightsView = ({
  entityId,
  view,
}: {
  entityId: string;
  view: InsightsView | null;
}): void => {
  const { setCurrentView, clearCurrentView } = useContext(
    InsightsContentContext,
  );

  // Filters and dates recompute their object identity freely (useDateScope and
  // drillDownsAsFixed both rebuild on unrelated renders), so publish on CONTENT
  // change rather than identity change — otherwise every report render churns the
  // context value and, with it, sendQuestion's identity.
  const contentKey = useMemo(
    () => (view ? JSON.stringify(view) : null),
    [view],
  );

  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    if (!contentKey || !viewRef.current) {
      return undefined;
    }
    setCurrentView(entityId, viewRef.current);
    // Id-guarded in the provider, so a component unmounting after a newer one
    // registered can't wipe the newer view.
    return () => clearCurrentView(entityId);
  }, [entityId, contentKey, setCurrentView, clearCurrentView]);
};

export default useRegisterInsightsView;
